import React, { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Heading,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@moonpig/launchpad-components'
import { CodeInput, TextInput } from '@moonpig/launchpad-forms'
import {
  LocaleTextDictionary,
  LocaleTextHook,
} from '@moonpig/web-core-locale-text'
import { useAuthFetch } from '@moonpig/web-core-auth'
import { useStore } from '@moonpig/web-core-stores'
import { createSha256Hash, sessionStore } from '@moonpig/web-core-utils'
import { useAuthenticationLocaleText } from '../../../../text-localisation'
import { authenticationService } from '../../AuthenticationService'
import { CodeGenerateError, LoginResult } from '../../types'

const formatTimer = (timer: number) => {
  return `${String(Math.floor(timer / 60)).padStart(2, '0')}:${String(
    timer % 60,
  ).padStart(2, '0')}`
}

export const ANALYTICS_MESSAGES = {
  sendCodeErrors: {
    unknownError: 'unspecified error sending code',
  },
  loginWithCodeErrors: {
    accountDisabled: 'account disabled',
    invalidCode: 'invalid code',
    expiredCode: 'expired code',
    unknownError: 'unspecified error logging in with code',
  },
}

const timeToResendCode = 30

const ResendCodeMessage: FC<{
  timer: number
  setTimer: (timer: number) => void
  localiseText: LocaleTextHook<LocaleTextDictionary<string>>
  resendLoginCodeHelper: () => void
  accountLocked: boolean
  loading: boolean
}> = ({
  timer,
  setTimer,
  localiseText,
  resendLoginCodeHelper,
  accountLocked,
  loading,
}) => {
  if (accountLocked) {
    return <></>
  }

  if (timer > 0) {
    return (
      <Text mb={6}>
        {localiseText('sign_in_with_code_form.resend_code_countdown')}&nbsp;
        <Text data-testid="countdown">{formatTimer(timer)}</Text>
      </Text>
    )
  }

  return (
    <Text mb={6}>
      {localiseText('sign_in_with_code_form.resend_code')}&nbsp;
      <TertiaryButton
        inline
        onClick={() => {
          setTimer(timeToResendCode)
          resendLoginCodeHelper()
        }}
        style={{ border: 0 }}
        disabled={loading}
      >
        {localiseText('sign_in_with_code_form.resend_code_link')}
      </TertiaryButton>
    </Text>
  )
}

type SignInWithCodeFormBaseProps = {
  email: string
  flowId?: string
  returnUrl?: string
  submitButtonText?: string
  loading?: boolean
  setLoading?: (loading: boolean) => void
  onSubmitCallback?: () => void
  onLoginSuccessCallback?: () => void
  onLoginErrorCallback?: (errorMessage: string) => void
  onResendCodeCallback?: () => void
  onSendCodeErrorCallback?: (errorMessage: string) => void
  flags?: Record<string, boolean>
}

type SignInWithCodeFormLoginProps = SignInWithCodeFormBaseProps & {
  action: 'login' | 'recovery'
}

type SignInWithCodeFormRegisterProps = SignInWithCodeFormBaseProps & {
  action: 'register'
  firstName: string
  lastName: string
}

export type SignInWithCodeFormProps =
  | SignInWithCodeFormLoginProps
  | SignInWithCodeFormRegisterProps

type error = {
  title?: string
  message?: string
  accountLocked?: boolean
}

export const SignInWithCodeForm: FC<SignInWithCodeFormProps> = ({
  action,
  email,
  onSendCodeErrorCallback,
  onLoginErrorCallback,
  onResendCodeCallback,
  onLoginSuccessCallback,
  onSubmitCallback,
  submitButtonText,
  flowId,
  loading = false,
  setLoading = () => {},
  flags = {},
  ...props
}) => {
  const localiseText = useAuthenticationLocaleText()
  const [error, setError] = useState<error | undefined>(undefined)
  const [timer, setTimer] = useState(timeToResendCode)
  const [isExpiredFlow, setIsExpiredFlow] = useState(false)
  const [resultFlowId, setResultFlowId] = useState<string>('')
  const [loginCode, setLoginCode] = useState<string>('')
  const authFetch = useAuthFetch()
  const isAccountLinking = !!flowId
  const isRegistration = action === 'register'
  const stateKey = isRegistration ? 'codeRegistrationState' : 'codeLoginState'

  const {
    store: { id: region },
  } = useStore()
  const { sendLoginCode, loginWithCode, registerWithCode } =
    authenticationService(authFetch, region)

  const featureCustomCodeInput = flags['customer-auth-custom-code-input']

  const expiredFlowErrorPayload = {
    title: localiseText('sign_in_with_code_error.expired_flow_title'),
    message: localiseText('sign_in_with_code_error.expired_flow_message'),
  }

  const missingCodeErrorPayload = {
    title: localiseText('sign_in_with_code_form.error_message_no_code_title'),
    message: localiseText(
      'sign_in_with_code_form.error_message_no_code_message',
    ),
  }

  const handleExpiredFlowError = (errorCode?: CodeGenerateError) => {
    if (!isAccountLinking) {
      setError({})
      return
    }

    if (errorCode === CodeGenerateError.FLOW_EXPIRED) {
      setIsExpiredFlow(true)
      setError(expiredFlowErrorPayload)
    } else {
      setIsExpiredFlow(false)
      setError({})
    }
  }

  useEffect(() => {
    if (isAccountLinking) return
    if (!resultFlowId) return

    const { flowId: storedFlowId } = sessionStore.getItem(stateKey) || {}
    if (storedFlowId === resultFlowId) {
      return
    }

    createSha256Hash(email).then(currentEmailHash => {
      sessionStore.setItem(stateKey, {
        flowId: resultFlowId,
        emailHash: currentEmailHash,
        createdDate: Date.now(),
      })
    })
  }, [resultFlowId, isAccountLinking, email, stateKey])

  const sendLoginCodeHelper = () => {
    setLoading(true)
    setLoginCode('')
    sendLoginCode({ email, action, flowId }).then(result => {
      const hasFlowId = 'flowId' in result

      if (hasFlowId) {
        setResultFlowId(result.flowId)
        setError(undefined)
      } else {
        if (onSendCodeErrorCallback) {
          onSendCodeErrorCallback(
            ANALYTICS_MESSAGES.sendCodeErrors.unknownError,
          )
        }
        handleExpiredFlowError(result.errorCode)
        setTimer(0)
      }
      setLoading(false)
    })
  }

  const resendLoginCodeHelper = () => {
    if (onResendCodeCallback) onResendCodeCallback()
    sendLoginCodeHelper()
  }

  const errorMessage = (result: LoginResult) => {
    if (result.accountLocked)
      return ANALYTICS_MESSAGES.loginWithCodeErrors.accountDisabled
    if (result.errorCode === 'invalid')
      return ANALYTICS_MESSAGES.loginWithCodeErrors.invalidCode
    if (result.errorCode === 'expired')
      return ANALYTICS_MESSAGES.loginWithCodeErrors.expiredCode
    return ANALYTICS_MESSAGES.loginWithCodeErrors.unknownError
  }

  const loginWithCodeHelper = () => {
    setError(undefined)
    if (!loginCode) {
      setError(missingCodeErrorPayload)
      return
    }
    if (onSubmitCallback) onSubmitCallback()
    setLoading(true)
    sessionStore.removeItem(stateKey)
    if (isRegistration) {
      const { firstName, lastName } = props as SignInWithCodeFormRegisterProps

      registerWithCode({
        flowId: resultFlowId,
        email,
        code: loginCode,
        firstName,
        lastName,
      }).then(result => {
        if (!result.success) {
          if (onLoginErrorCallback) onLoginErrorCallback(errorMessage(result))
          setError({
            title: result.errorTitle,
            message: result.errorMessage,
          })
          setLoading(false)
        } else if (onLoginSuccessCallback) onLoginSuccessCallback()
      })
    } else {
      loginWithCode({
        flowId: resultFlowId,
        email,
        code: loginCode,
      }).then(result => {
        if (!result.success) {
          if (onLoginErrorCallback) onLoginErrorCallback(errorMessage(result))
          setError({
            title: result.errorTitle,
            message: result.errorMessage,
            accountLocked: result.accountLocked,
          })
          setLoading(false)
        } else if (onLoginSuccessCallback) onLoginSuccessCallback()
      })
    }
  }

  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer(prevTimer => prevTimer - 1)
      }, 1000)

      return () => clearInterval(intervalId)
    }
  }, [timer])

  useEffect(() => {
    if (isAccountLinking) {
      sessionStore.removeItem(stateKey)
      sendLoginCodeHelper()
      return
    }

    const {
      flowId: storedFlowId,
      emailHash: storedEmailHash,
      createdDate,
    } = sessionStore.getItem(stateKey) || {}

    createSha256Hash(email).then(currentEmailHash => {
      const tenMinutes = 10 * 60 * 1000
      const tenMinutesAgo = Date.now() - tenMinutes

      if (
        storedFlowId &&
        currentEmailHash === storedEmailHash &&
        createdDate > tenMinutesAgo
      ) {
        setResultFlowId(storedFlowId)
        const secondsElapsed = Math.floor((Date.now() - createdDate) / 1000)
        const secondsRemaining = timeToResendCode - secondsElapsed
        setTimer(Math.max(secondsRemaining, 0))
        return
      }

      sendLoginCodeHelper()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, isAccountLinking])

  const isLocked = error?.accountLocked ?? false
  const validCode = loginCode && loginCode.length === 6
  const canContinue = !!resultFlowId && !!validCode && !isLocked && !loading
  const disableInput = isAccountLinking && isExpiredFlow

  const FlaggedCodeInput = featureCustomCodeInput ? (
    <CodeInput
      name="loginCode"
      disabled={disableInput}
      label={localiseText('sign_in_with_code_form.code_label')}
      value={loginCode}
      onValueChange={(value: string) => {
        setError(undefined)
        setLoginCode(value)
      }}
      autoSubmit
      onAutoSubmit={loginWithCodeHelper}
      mb={6}
    />
  ) : (
    <TextInput
      name={'loginCode'}
      disabled={disableInput}
      label={localiseText('sign_in_with_code_form.code_label')}
      value={loginCode}
      placeholder={localiseText('sign_in_with_code_form.code_placeholder')}
      mb={6}
      autoComplete="one-time-code"
      onChange={e => {
        const trimmedWhitespace = e.target.value.replace(/\s+/g, '')
        setLoginCode(trimmedWhitespace.slice(0, 6))
      }}
    />
  )

  return (
    <Box>
      {FlaggedCodeInput}
      {error && (
        <Alert
          variant="error"
          mb={6}
          aria-describedby={'sign-in-with-code-form-error'}
        >
          <Heading
            level="h5"
            mt={2}
            mb={2}
            typography={'typeBodyLabel'}
            color={'colorTextBody'}
          >
            {error.title ||
              localiseText('sign_in_with_code_form.error_heading')}
          </Heading>
          <Text typography={'typeBodyText'}>
            {error.message ||
              localiseText('sign_in_with_code_form.error_message')}
          </Text>
        </Alert>
      )}

      <PrimaryButton
        mb={8}
        disabled={!featureCustomCodeInput && (!canContinue || disableInput)}
        width="100%"
        onClick={loginWithCodeHelper}
      >
        {submitButtonText ??
          localiseText('sign_in_with_code_form.submit_button_text')}
      </PrimaryButton>
      <ResendCodeMessage
        timer={timer}
        setTimer={setTimer}
        localiseText={localiseText}
        resendLoginCodeHelper={resendLoginCodeHelper}
        accountLocked={isLocked || disableInput}
        loading={loading}
      />
    </Box>
  )
}
